import axiosInstance from '../utils/api';

/**
 * Helper to convert base64 to File
 */
function base64ToFile(base64String, filename, mimeType) {
  const byteString = atob(base64String.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], filename, { type: mimeType });
}

/**
 * Helper to process and append an image to FormData
 * Returns the image name if image was appended, null otherwise
 */
function processAndAppendImage(formData, imageValue, imageName, sectionIndex) {
  if (!imageValue) return null;
  
  let imageFile = imageValue;
  
  // Handle File objects directly
  if (imageValue instanceof File) {
    formData.append("content_post[attachments][]", imageFile);
    return imageName; // Return image name for metafields
  }
  
  // Handle base64 images
  if (typeof imageValue === "string" && imageValue.startsWith("data:image/")) {
    const mimeMatch = imageValue.match(/^data:(image\/[a-zA-Z]+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : "image/png";
    const extension = mimeType.split("/")[1];
    const filename = `${imageName}_${sectionIndex}.${extension}`;
    imageFile = base64ToFile(imageValue, filename, mimeType);
    formData.append("content_post[attachments][]", imageFile);
    return imageName; // Return image name for metafields
  }
  
  // Skip regular URL images - they're already stored on server
  if (typeof imageValue === "string" && !imageValue.startsWith("data:")) {
    console.log("Skipping already-uploaded image URL:", imageValue);
    return null;
  }
  
  return null;
}

/**
 * Save content post as draft
 * @param {Object} contentData - Content data to save
 * @param {Array} emailSections - Email sections array (for email content type with images)
 * @returns {Promise} API response
 */
export const saveContentPostDraft = async (contentData, emailSections = []) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Unauthorized: No token found');
    }

    const formData = new FormData();
    
    // Append required content post fields
    if (contentData.title) formData.append('content_post[title]', contentData.title);
    if (contentData.description) formData.append('content_post[description]', contentData.description);
    if (contentData.post_type) formData.append('content_post[post_type]', contentData.post_type);
    if (contentData.session_id) formData.append('content_post[session_id]', contentData.session_id);
    if (contentData.agent_id) formData.append('content_post[agent_id]', contentData.agent_id);
    if (contentData.generated_content) formData.append('content_post[generated_content]', contentData.generated_content);
    
    // Parse existing metafields to merge with image names
    let metafieldsObj = {};
    if (contentData.metafields) {
      try {
        metafieldsObj = typeof contentData.metafields === 'string' 
          ? JSON.parse(contentData.metafields) 
          : contentData.metafields;
      } catch (e) {
        console.error('Error parsing existing metafields:', e);
      }
    }

    // Process images from email sections and collect image names for metafields
    const imageNames = [];
    if (emailSections && emailSections.length > 0) {
      for (let i = 0; i < emailSections.length; i++) {
        const section = emailSections[i];
        // Use section.id for unique naming (prevents conflicts between multiple sections of same component type)
        // Generate stable ID using same format as in DiyAgentPlayground: email-section-{index}-{component_name}
        const sectionId = section?.id || `email-section-${i}-${section?.component_name || 'text'}`;

        // Handle main image field
        if (section?.image) {
          const imageName = processAndAppendImage(formData, section.image, sectionId, i);
          if (imageName) imageNames.push(imageName);
        }
        
        // Handle Title_4Img component's multiple image fields (image1, image2, image3, image4)
        ['image1', 'image2', 'image3', 'image4'].forEach((imgKey, imgIndex) => {
          if (section?.[imgKey]) {
            const imageName = processAndAppendImage(formData, section[imgKey], `${sectionId}_${imgKey}`, i);
            if (imageName) imageNames.push(imageName);
          }
        });
        
        // Handle features array images (for components like 2COL_3ROWS_Benefits)
        if (section?.features && Array.isArray(section.features)) {
          section.features.forEach((feature, featureIndex) => {
            // Process image field (preferred)
            if (feature?.image) {
              const imageName = processAndAppendImage(formData, feature.image, `${sectionId}_feature_${featureIndex}_image`, i);
              if (imageName) imageNames.push(imageName);
            }
            // Also process image_placeholder if image is not set
            else if (feature?.image_placeholder) {
              const imageName = processAndAppendImage(formData, feature.image_placeholder, `${sectionId}_feature_${featureIndex}_image`, i);
              if (imageName) imageNames.push(imageName);
            }
          });
        }
      }
    }
    
    // Add image names array to metafields
    if (imageNames.length > 0) {
      metafieldsObj.image_section_names = imageNames;
    }
    
    // Append metafields (with or without image names)
    formData.append('content_post[metafields]', JSON.stringify(metafieldsObj));

    const response = await axiosInstance.post('/api/v1/content_posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error saving content post draft:', error);
    throw error;
  }
};

/**
 * Update existing content post draft
 * @param {number|string} postId - ID of the post to update
 * @param {Object} contentData - Updated content data
 * @param {Array} emailSections - Email sections array (for email content type with images)
 * @returns {Promise} API response
 */
export const updateContentPostDraft = async (postId, contentData, emailSections = []) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Unauthorized: No token found');
    }

    const formData = new FormData();
    
    // Append required content post fields
    if (contentData.title) formData.append('content_post[title]', contentData.title);
    if (contentData.description) formData.append('content_post[description]', contentData.description);
    if (contentData.post_type) formData.append('content_post[post_type]', contentData.post_type);
    if (contentData.session_id) formData.append('content_post[session_id]', contentData.session_id);
    if (contentData.agent_id) formData.append('content_post[agent_id]', contentData.agent_id);
    if (contentData.generated_content) formData.append('content_post[generated_content]', contentData.generated_content);
    
    // Parse existing metafields to merge with image names
    let metafieldsObj = {};
    if (contentData.metafields) {
      try {
        metafieldsObj = typeof contentData.metafields === 'string' 
          ? JSON.parse(contentData.metafields) 
          : contentData.metafields;
      } catch (e) {
        console.error('Error parsing existing metafields:', e);
      }
    }

    // Process images from email sections and collect image names for metafields
    const imageNames = [];
    if (emailSections && emailSections.length > 0) {
      for (let i = 0; i < emailSections.length; i++) {
        const section = emailSections[i];
        // Use section.id for unique naming (prevents conflicts between multiple sections of same component type)
        // Generate stable ID using same format as in DiyAgentPlayground: email-section-{index}-{component_name}
        const sectionId = section?.id || `email-section-${i}-${section?.component_name || 'text'}`;

        // Handle main image field
        if (section?.image) {
          const imageName = processAndAppendImage(formData, section.image, sectionId, i);
          if (imageName) imageNames.push(imageName);
        }
        
        // Handle Title_4Img component's multiple image fields (image1, image2, image3, image4)
        ['image1', 'image2', 'image3', 'image4'].forEach((imgKey, imgIndex) => {
          if (section?.[imgKey]) {
            const imageName = processAndAppendImage(formData, section[imgKey], `${sectionId}_${imgKey}`, i);
            if (imageName) imageNames.push(imageName);
          }
        });
        
        // Handle features array images (for components like 2COL_3ROWS_Benefits)
        if (section?.features && Array.isArray(section.features)) {
          section.features.forEach((feature, featureIndex) => {
            // Process image field (preferred)
            if (feature?.image) {
              const imageName = processAndAppendImage(formData, feature.image, `${sectionId}_feature_${featureIndex}_image`, i);
              if (imageName) imageNames.push(imageName);
            }
            // Also process image_placeholder if image is not set
            else if (feature?.image_placeholder) {
              const imageName = processAndAppendImage(formData, feature.image_placeholder, `${sectionId}_feature_${featureIndex}_image`, i);
              if (imageName) imageNames.push(imageName);
            }
          });
        }
      }
    }
    
    // Add image names array to metafields
    if (imageNames.length > 0) {
      metafieldsObj.image_section_names = imageNames;
    }
    
    // Append metafields (with or without image names)
    formData.append('content_post[metafields]', JSON.stringify(metafieldsObj));

    const response = await axiosInstance.put(`/api/v1/content_posts/${postId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error updating content post draft:', error);
    throw error;
  }
};

/**
 * Get content post by ID
 * @param {number|string} postId - ID of the post to fetch
 * @returns {Promise} API response
 */
export const getContentPostById = async (postId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Unauthorized: No token found');
    }

    const response = await axiosInstance.get(`/api/v1/content_posts/${postId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching content post by ID:', error);
    throw error;
  }
};

export default {
  saveContentPostDraft,
  updateContentPostDraft,
  getContentPostById,
};

